import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('thriftCart');
    try {
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (e) {
      return [];
    }
  });

  const [wishlistItems, setWishlistItems] = useState(() => {
    const savedWish = localStorage.getItem('thriftWishlist');
    try {
      return savedWish ? JSON.parse(savedWish) : [];
    } catch (e) {
      return [];
    }
  });

  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    localStorage.setItem('thriftCart', JSON.stringify(cartItems));
    localStorage.setItem('thriftWishlist', JSON.stringify(wishlistItems));
  }, [cartItems, wishlistItems]);

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('thriftCart');
  };

  // 🔥 Helper: Kisi bhi product ke specific size ka stock nikalne ke liye
  const getSizeSpecificStock = (product, targetSize) => {
    if (!product.sizeStock) return 0;
    const sizeData = product.sizeStock.find(s => s.size === targetSize);
    return sizeData ? sizeData.quantity : 0;
  };

  const addToCart = (product, size) => {
    const productId = product._id || product.id;
    const finalSize = size || (product.sizes && product.sizes.length > 0 ? product.sizes[0] : "FREE SIZE");

    // 🔥 Check size-specific stock
    const availableStock = getSizeSpecificStock(product, finalSize);

    if (availableStock <= 0) {
      setNotification({ show: true, message: `Size ${finalSize} is currently Sold Out!`, type: "error" });
      setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
      return;
    }

    setCartItems((prev) => {
      const isExist = prev.find((item) => (item._id || item.id) === productId && item.selectedSize === finalSize);
      
      if (isExist) {
        if (isExist.quantity >= availableStock) {
          setNotification({ show: true, message: "Max stock for this size reached!", type: "error" });
          return prev;
        }

        return prev.map((item) =>
          ((item._id || item.id) === productId && item.selectedSize === finalSize) 
          ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      // Product object ke saath uska current size-wise stock bhi save kar rahe hain reference ke liye
      return [...prev, { ...product, id: productId, quantity: 1, selectedSize: finalSize, currentSizeStock: availableStock }];
    });

    setNotification({ show: true, message: `${product.name} (${finalSize}) added to bag!`, type: "success" });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  const updateQuantity = (id, size, delta) => {
    setCartItems((prev) =>
      prev.map((item) => {
        if ((item._id || item.id) === id && item.selectedSize === size) {
          const newQuantity = item.quantity + delta;
          
          // 🔥 Use saved currentSizeStock or product.sizeStock
          const maxStock = item.currentSizeStock || getSizeSpecificStock(item, size);

          if (delta > 0 && item.quantity >= maxStock) {
            setNotification({ show: true, message: `Only ${maxStock} items available in this size!`, type: "error" });
            setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 2000);
            return item; 
          }

          return { ...item, quantity: Math.max(1, newQuantity) };
        }
        return item;
      })
    );
  };

  const removeFromCart = (id, size) => {
    setCartItems((prev) => prev.filter((item) => !((item._id || item.id) === id && item.selectedSize === size)));
  };

  const toggleWishlist = (product) => {
    setWishlistItems((prev) => {
      // Sahi logic: product id match honi chahiye
      const productId = product._id || product.id;
      const isExist = prev.find((item) => (item._id || item.id) === productId);

      if (isExist) {
        // Agar pehle se hai toh remove karo
        return prev.filter((item) => (item._id || item.id) !== productId);
      } else {
        // Agar nahi hai toh add karo (img field ensure kar rahe hain taaki wishlist page pe dikhe)
        return [...prev, { ...product, id: productId, img: product.img || product.image }];
      }
    });
  };

  // 🔥 Validate stock using sizeStock array from DB
  const validateStockBeforeCheckout = (productsFromDB) => {
    const issues = cartItems.filter(item => {
      const dbProduct = productsFromDB.find(p => p._id === (item._id || item.id));
      if (!dbProduct) return true;
      const dbSizeStock = getSizeSpecificStock(dbProduct, item.selectedSize);
      return dbSizeStock <= 0 || item.quantity > dbSizeStock;
    });
    return issues.length === 0;
  };

  const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const subtotal = cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

  return (
    <CartContext.Provider value={{ 
      cartItems, addToCart, removeFromCart, updateQuantity, cartCount, subtotal,
      wishlistItems, toggleWishlist, notification, clearCart, validateStockBeforeCheckout 
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
};